/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { LoggerStore } from "../../logger/logger-store"
import type { Result } from "../../network/http-client"
import { makeDeleteRequest, makeGetRequest, makePutRequest } from "../../network/http-client"

import type { UserDetailStore } from "./user-detail-store"
import { UserRole } from "./user-types"

enum Endpoint {
    user = "/api/private/user"
}

export class UserRoleService {
    constructor(
        private readonly store: UserDetailStore,
        private readonly logger: LoggerStore
    ) {}

    async getUserRoles(userId: number): Promise<Result<UserRole[]>> {
        const roles = await makeGetRequest<string[]>(`${Endpoint.user}/${userId}/role`, {})
        if (roles.success) {
            const userRoles = (roles.data ?? []).filter(isUserRole)
            this.store.setUserDetailRoles(userRoles)
            return {
                success: true,
                data: userRoles
            }
        }
        if (!roles.success) {
            this.logger.logApiError("Get user roles failed", roles.apiError, roles.error)
            return roles
        }
        return roles
    }

    async assignUserRole(userId: number, role: UserRole): Promise<Result<void>> {
        const res = await makePutRequest<UserRole, void>(`${Endpoint.user}/${userId}/role/${role}`, {})
        if (res.success) {
            const newRoles = [...this.store.userDetailRoles, role]
            this.store.setUserDetailRoles(newRoles)
            this.logger.logOk("User role assigned successfully")
        }
        if (!res.success) {
            this.logger.logApiError("Assign user role failed", res.apiError, res.error)
        }
        return res
    }

    async removeUserRole(userId: number, role: UserRole): Promise<Result<void>> {
        const res = await makeDeleteRequest<void>(`${Endpoint.user}/${userId}/role/${role}`, {})
        if (res.success) {
            const newRoles = this.store.userDetailRoles.filter((r) => r !== role)
            this.store.setUserDetailRoles(newRoles)
            this.logger.logOk("User role removed successfully")
        }
        if (!res.success) {
            this.logger.logApiError("Remove user role failed", res.apiError, res.error)
        }
        return res
    }
}

// method checks if strings coming from backend are those defined in Swagger
// and convert them to UserRole
const isUserRole = (role: string): role is UserRole => Object.values(UserRole).includes(role as UserRole)
